import { useTranslation } from 'react-i18next'

/** Which of the stored levers belong to which element, and in what order they are offered. */
const SIZES = ['small', 'normal', 'large']

/*
 * Written out rather than built from a stem. `posts.barAlign${how}` reads the same to a person and
 * is invisible to the test that checks every key a component asks for exists in every language —
 * which is how four keys once shipped saying their own name to the owner of a shop.
 */
const ALIGNS = [
  { how: 'left', glyph: '⯇', word: 'posts.barAlignLeft' },
  { how: 'center', glyph: '≡', word: 'posts.barAlignCenter' },
  { how: 'right', glyph: '⯈', word: 'posts.barAlignRight' },
]

const INKS = [
  { ink: 'brand', word: 'posts.barInkBrand' },
  { ink: 'white', word: 'posts.barInkWhite' },
  { ink: 'ink', word: 'posts.barInkInk' },
]

/**
 * One key. Outside the bar rather than inside it: a component declared in a render is a new
 * component every render, and the button that was just tapped is unmounted out from under the
 * focus that was on it.
 */
function Key({ label, on, title, busy, onPick }) {
  return (
    <button
      type="button"
      className={`wordbar__key${on ? ' wordbar__key--on' : ''}`}
      disabled={busy}
      title={title}
      aria-label={title}
      aria-pressed={on}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onPick}
    >
      {label}
    </button>
  )
}

/**
 * The levers over the element that is held, the way a drawing program puts them over the shape you
 * picked up rather than in a panel somewhere else.
 *
 * A button shows what was ASKED FOR, not what is drawn. Nothing pressed means nothing was asked,
 * and the look's own answer stands — which is why the first tap on the weight always sets it heavy
 * even over a look that draws it heavy anyway. The picture redraws on every tap, so what the answer
 * did is a second away rather than a thing to be read off a button.
 *
 * The price has no ranging and no case: it is one line of digits in every look, and a button with
 * nothing to move is a button that lies.
 */
export default function WordBar({ element, design, at, busy, onSet }) {
  const { t } = useTranslation()

  const of = (lever) => design?.[`${element}${lever}`] ?? null
  const set = (lever, value) => onSet?.({ [`${element}${lever}`]: value })

  const size = of('Size') ?? 'normal'
  const stepSize = (by) => set('Size', SIZES[Math.min(SIZES.length - 1, Math.max(0, SIZES.indexOf(size) + by))])

  const words = element === 'price' ? null : true

  // Above the words, unless they sit so high that above is off the picture. Across the middle of
  // the frame rather than over the element itself: a bar hung off a name in the corner hangs half
  // of itself off the picture, and these previews are the width of a phone.
  const below = at.y < 0.22

  return (
    <div
      className={`wordbar${below ? ' wordbar--below' : ''}`}
      style={{ top: `${(below ? at.y + at.height : at.y) * 100}%` }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Key
        busy={busy}
        label="Б"
        on={of('Bold') === true}
        title={t('posts.barBold')}
        onPick={() => set('Bold', !of('Bold'))}
      />

      {words && (
        <Key
          busy={busy}
          label="AA"
          on={of('Caps') === true}
          title={t('posts.barCaps')}
          onPick={() => set('Caps', !of('Caps'))}
        />
      )}

      <span className="wordbar__gap" />

      <Key busy={busy} label="A−" on={false} title={t('posts.barSmaller')} onPick={() => stepSize(-1)} />
      <Key busy={busy} label="A+" on={false} title={t('posts.barBigger')} onPick={() => stepSize(1)} />

      {words && (
        <>
          <span className="wordbar__gap" />

          {ALIGNS.map(({ how, glyph, word }) => (
            <Key
              key={how}
              busy={busy}
              label={glyph}
              on={of('Align') === how}
              title={t(word)}
              onPick={() => set('Align', how)}
            />
          ))}
        </>
      )}

      <span className="wordbar__gap" />

      {INKS.map(({ ink, word }) => (
        <Key
          key={ink}
          busy={busy}
          label={<span className={`wordbar__ink wordbar__ink--${ink}`} />}
          on={of('Ink') === ink}
          title={t(word)}
          onPick={() => set('Ink', ink)}
        />
      ))}
    </div>
  )
}
